import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../../api/axiosInstance';

const emptyConfig = {
  enabled: false,
  apiUrl: 'https://api.level.io',
  apiKey: '',
};

function formatDate(value) {
  if (!value) return 'Jamais';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function LevelSettingsPage() {
  const [config, setConfig] = useState(emptyConfig);
  const [groups, setGroups] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [syncSummary, setSyncSummary] = useState(null);

  const loadConfig = async () => {
    const { data } = await axios.get('/api/settings/level/config');
    setConfig((current) => ({ ...emptyConfig, ...data, apiKey: current.apiKey || '' }));
  };

  const loadGroups = async ({ refresh = false } = {}) => {
    setGroupsLoading(true);
    try {
      const { data } = await axios.get(`/api/settings/level/groups?refresh=${refresh}`);
      setOrganizations(data.organizations || []);
      setGroups((data.groups || []).map((group) => ({
        ...group,
        organization: group.mapping?.organization || null,
        suggestedOrganization: group.suggestedOrganization || null,
        enabled: group.mapping?.enabled ?? true,
        notes: group.mapping?.notes || '',
        suggested: Boolean(group.suggestedOrganization && !group.mapping?.organization),
      })));
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger les dossiers Level.io.' });
    } finally {
      setGroupsLoading(false);
    }
  };

  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const { data } = await axios.get('/api/settings/level/devices');
      setDevices(data.devices || []);
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger les devices Level.io.' });
    } finally {
      setDevicesLoading(false);
    }
  };

  const syncDevices = async () => {
    setDevicesLoading(true);
    setFeedback(null);
    setSyncSummary(null);
    try {
      const { data } = await axios.post('/api/level/devices/sync');
      setSyncSummary(data);
      const unmappedCount = data.unmappedDevices || 0;
      setFeedback({
        type: unmappedCount ? 'warning' : 'success',
        message: unmappedCount
          ? `Synchronisation terminée. ${unmappedCount} device(s) sans mapping client.`
          : 'Synchronisation terminée. Tous les devices sont mappés.',
      });
      await Promise.all([loadGroups(), loadDevices()]);
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Synchronisation des devices impossible.' });
    } finally {
      setDevicesLoading(false);
    }
  };

  const hydrate = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadConfig(), loadGroups()]);
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger Level.io.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const saveConfig = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axios.put('/api/settings/level/config', {
        enabled: config.enabled,
        apiUrl: config.apiUrl,
        apiKey: config.apiKey,
      });
      setConfig({ ...data, apiKey: '' });
      setFeedback({ type: 'success', message: 'Configuration Level.io sauvegardée.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Sauvegarde impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const testConfig = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      const { data } = await axios.post('/api/settings/level/test');
      setConfig((current) => ({ ...current, ...data, apiKey: '' }));
      setFeedback({ type: 'success', message: data.lastTestMessage || 'Connexion Level.io OK.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.error || 'Connexion Level.io impossible.' });
    } finally {
      setTesting(false);
    }
  };

  const saveMappings = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = groups
        .map((group) => ({
          levelGroupId: group.id,
          levelGroupName: group.name,
          levelGroupPath: group.path,
          organization: group.organization?._id || null,
          enabled: group.enabled,
          notes: group.notes,
        }));
      await axios.put('/api/settings/level/mappings', { mappings: payload });
      setFeedback({ type: 'success', message: 'Mapping Level.io sauvegardé.' });
      await loadGroups();
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de sauvegarder le mapping.' });
    } finally {
      setSaving(false);
    }
  };

  const updateGroup = (id, patch) => {
    setGroups((current) => current.map((group) => group.id === id ? { ...group, ...patch } : group));
  };

  const groupColumns = useMemo(() => [
    { accessorKey: 'path', header: 'Dossier Level.io', size: 260 },
    {
      accessorKey: 'organization.name',
      header: 'Client',
      size: 320,
      Cell: ({ row }) => (
        <Stack spacing={0.75}>
          <Autocomplete
            size="small"
            options={organizations}
            value={row.original.organization || null}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            onChange={(_, value) => updateGroup(row.original.id, { organization: value, suggested: false })}
            renderInput={(params) => <TextField {...params} placeholder="Aucun client" />}
          />
          {!row.original.organization?._id && row.original.suggestedOrganization?._id ? (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" color="info" variant="outlined" label={`Suggestion : ${row.original.suggestedOrganization.name}`} />
              <Button
                size="small"
                variant="text"
                onClick={() => updateGroup(row.original.id, { organization: row.original.suggestedOrganization, enabled: true, suggested: false })}
              >
                Utiliser
              </Button>
              <Button
                size="small"
                color="inherit"
                variant="text"
                onClick={() => updateGroup(row.original.id, { organization: null, enabled: false, suggested: false })}
              >
                Ignorer
              </Button>
            </Stack>
          ) : null}
        </Stack>
      ),
    },
    {
      accessorKey: 'mappingStatus',
      header: 'Statut',
      size: 110,
      Cell: ({ row }) => {
        if (row.original.enabled === false) return <Chip size="small" color="default" label="Ignoré" />;
        if (row.original.organization?._id) return <Chip size="small" color="success" label="Mappé" />;
        if (row.original.suggestedOrganization?._id) return <Chip size="small" color="info" variant="outlined" label="Suggestion" />;
        return <Chip size="small" color="warning" label="À mapper" />;
      },
    },
    {
      accessorKey: 'enabled',
      header: 'Mapper',
      size: 80,
      Cell: ({ row }) => (
        <Switch
          checked={Boolean(row.original.enabled)}
          onChange={(event) => updateGroup(row.original.id, { enabled: event.target.checked })}
        />
      ),
    },
    {
      accessorKey: 'notes',
      header: 'Note',
      size: 220,
      Cell: ({ row }) => (
        <TextField
          size="small"
          fullWidth
          value={row.original.notes || ''}
          onChange={(event) => updateGroup(row.original.id, { notes: event.target.value })}
        />
      ),
    },
  ], [organizations]);

  const deviceColumns = useMemo(() => [
    { accessorKey: 'name', header: 'Nom' },
    { accessorKey: 'hostname', header: 'Hostname' },
    { accessorKey: 'serial_number', header: 'Série' },
    { accessorKey: 'matchedOrganization.name', header: 'Client matché' },
  ], []);

  const groupTable = useMaterialReactTable({
    columns: groupColumns,
    data: groups,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    initialState: { showColumnFilters: true, showGlobalFilter: true, density: 'compact' },
    state: { isLoading: groupsLoading },
  });

  const deviceTable = useMaterialReactTable({
    columns: deviceColumns,
    data: devices,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    initialState: { showColumnFilters: true, showGlobalFilter: true, density: 'compact' },
    state: { isLoading: devicesLoading },
  });

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={2.5}>
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
      {syncSummary?.unmappedGroups?.length ? (
        <Alert severity="warning">
          Groupes à mapper : {syncSummary.unmappedGroups.map((group) => group.levelGroupPath || group.levelGroupName).join(', ')}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Level.io</Typography>
              <Typography variant="body2" color="text.secondary">
                Clé API globale et mapping des dossiers Level.io vers les clients.
              </Typography>
            </Box>
            <Chip label={config.enabled ? 'Activé' : 'Désactivé'} color={config.enabled ? 'success' : 'default'} />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              label="API URL"
              size="small"
              value={config.apiUrl || ''}
              onChange={(event) => setConfig((current) => ({ ...current, apiUrl: event.target.value }))}
              sx={{ minWidth: { md: 260 } }}
            />
            <TextField
              label={config.hasApiKey ? `API key (${config.apiKeyMasked})` : 'API key'}
              size="small"
              type="password"
              value={config.apiKey || ''}
              onChange={(event) => setConfig((current) => ({ ...current, apiKey: event.target.value }))}
              sx={{ minWidth: { md: 320 } }}
              placeholder={config.hasApiKey ? 'Laisser vide pour conserver' : ''}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Actif</Typography>
              <Switch
                checked={Boolean(config.enabled)}
                onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked }))}
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={saveConfig} disabled={saving}>Sauvegarder</Button>
            <Button variant="outlined" onClick={testConfig} disabled={testing || !config.hasApiKey}>{testing ? 'Test...' : 'Tester'}</Button>
            <Button variant="outlined" onClick={() => loadGroups({ refresh: true })} disabled={groupsLoading || !config.hasApiKey}>Resynchroniser les dossiers</Button>
            <Button variant="outlined" onClick={syncDevices} disabled={devicesLoading || !config.hasApiKey}>Synchroniser les devices</Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Dernier test : {formatDate(config.lastTestAt)} {config.lastTestMessage ? `- ${config.lastTestMessage}` : ''}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>Mapping dossiers</Typography>
            <Chip label={`${groups.filter((group) => group.organization?._id).length} mappé(s)`} color="success" variant="outlined" />
            <Chip label={`${groups.filter((group) => !group.organization?._id).length} à mapper`} color="warning" variant="outlined" />
            <Button variant="contained" onClick={saveMappings} disabled={saving || !groups.length}>Sauvegarder le mapping</Button>
          </Stack>
          <MaterialReactTable table={groupTable} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Devices</Typography>
          <MaterialReactTable table={deviceTable} />
        </Stack>
      </Paper>
    </Stack>
  );
}
